import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, TrendingUp, TrendingDown, DollarSign, BarChart2, Plus, RefreshCw, Wallet } from 'lucide-react';
import { fetchTradesFromDB, sendTradeToDB } from '../journaling/JournalingService';
import AddTradeModal from '../trades/AddTradeModal';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../stores/AuthContext';
import { getDashboardAnalytics, getGoals } from '../../services/dashboardAnalytics';
import GoalTracker from './widgets/GoalTracker';
import PlanAdherence from './widgets/PlanAdherence';
import TodaySession from './widgets/TodaySession';
import EquityCurve from './widgets/EquityCurve';
import AccountEquityCard from './widgets/AccountEquityCard';
import DrawdownWidget from './widgets/DrawdownWidget';
import RiskPerTradeWidget from './widgets/RiskPerTradeWidget';

const Card = ({ children, className = '' }) => (
  <div className={`bg-card text-card-foreground rounded-xl shadow-sm border border-border ${className}`}>{children}</div>
);

const SectionLabel = ({ children }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <span className="h-3.5 w-0.5 rounded-full bg-primary inline-block" />
    <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{children}</span>
  </div>
);

const Dashboard = () => {
  const { user } = useAuth();
  const [trades, setTrades] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [stats, setStats] = useState({
    totalTrades: 0,
    totalProfit: 0,
    winRate: 0,
    averagePips: 0
  });
  const [dashboardData, setDashboardData] = useState(null);
  const [goals, setGoals] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const formatProfit = (profit) => {
    if (profit === null || profit === undefined) return '0.00';
    const numProfit = Number(profit);
    if (isNaN(numProfit)) return '0.00';
    return numProfit.toFixed(2);
  };

  const isPositiveProfit = (profit) => {
    if (profit === null || profit === undefined) return false;
    const numProfit = Number(profit);
    return !isNaN(numProfit) && numProfit > 0;
  };

  const handleAddTrade = async (newTrade) => {
    try {
      await sendTradeToDB(newTrade);
      await loadTrades();
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Error adding trade:', error);
      alert(`Failed to add trade: ${error.message}`);
    }
  };

  useEffect(() => {
    loadTrades();
    loadDashboardData();
  }, []);

  const loadDashboardData = async (refresh = false) => {
    try {
      if (refresh) setRefreshing(true);
      const [analyticsResult, goalsResult] = await Promise.all([
        getDashboardAnalytics(refresh).catch(() => null),
        getGoals().catch(() => ({ goals: [] })),
      ]);
      if (analyticsResult?.success) {
        setDashboardData(analyticsResult);
      }
      setGoals(goalsResult?.goals || []);
    } catch (error) {
      console.error('Error loading dashboard analytics:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadDashboardData(true);
  };

  const loadTrades = async () => {
    try {
      const tradesData = await fetchTradesFromDB();
      const processedTrades = tradesData.map(trade => ({
        ...trade,
        profit: trade.profit ? Number(trade.profit) : 0
      }));
      setTrades(processedTrades);
      calculateStats(processedTrades);
    } catch (error) {
      console.error('Error loading trades:', error);
    }
  };

  const calculateStats = (tradesData) => {
    const totalTrades = tradesData.length;
    const totalProfit = tradesData.reduce((sum, trade) => sum + (Number(trade.profit) || 0), 0);
    const winningTrades = tradesData.filter(trade => Number(trade.profit) > 0).length;
    const totalPips = tradesData.reduce((sum, trade) => sum + (Number(trade.pips) || 0), 0);
    setStats({
      totalTrades,
      totalProfit,
      winRate: totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0,
      averagePips: totalTrades > 0 ? totalPips / totalTrades : 0
    });
  };

  const statsCards = [
    {
      icon: <TrendingUp className="w-4 h-4" />,
      title: 'Avg. Pips',
      value: stats.averagePips.toFixed(1),
      iconBg: 'bg-[#00D4C8]/10',
      iconColor: 'text-[#00D4C8]',
      valueColor: '',
    },
    {
      icon: <DollarSign className="w-4 h-4" />,
      title: 'Total P&L',
      value: `${stats.totalProfit >= 0 ? '+' : ''}$${formatProfit(stats.totalProfit)}`,
      iconBg: stats.totalProfit >= 0 ? 'bg-[rgba(34,197,94,0.10)]' : 'bg-[rgba(239,68,68,0.10)]',
      iconColor: stats.totalProfit >= 0 ? 'text-[#4ADE80]' : 'text-[#F87171]',
      valueColor: stats.totalProfit >= 0 ? 'text-[#4ADE80]' : 'text-[#F87171]',
    },
    {
      icon: <BarChart2 className="w-4 h-4" />,
      title: 'Win Rate',
      value: stats.winRate.toFixed(1) + '%',
      iconBg: 'bg-[#00D4C8]/10',
      iconColor: 'text-[#00D4C8]',
      valueColor: stats.winRate >= 50 ? 'text-[#4ADE80]' : 'text-[#F87171]',
    },
    {
      icon: <Calendar className="w-4 h-4" />,
      title: 'Total Trades',
      value: stats.totalTrades,
      iconBg: 'bg-secondary',
      iconColor: 'text-muted-foreground',
      valueColor: '',
    },
  ];

  return (
    <div className="pt-16 min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="border-b border-border bg-background/80 backdrop-blur sticky top-16 z-10">
        <div className="px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <h1 className="text-xl font-semibold tracking-tight">Pulse</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-all duration-150 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <Button onClick={() => setIsAddModalOpen(true)} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              <span>New Trade</span>
            </Button>
          </div>
        </div>
      </div>

      {isAddModalOpen && (
        <AddTradeModal
          onClose={() => setIsAddModalOpen(false)}
          onSave={handleAddTrade}
        />
      )}

      <div className="px-4 sm:px-6 lg:px-8 py-8 space-y-10">

        {/* ── Balance Setup Banner ── */}
        {!user?.starting_balance && (
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Wallet className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Set your starting balance</p>
                <p className="text-xs text-muted-foreground">Enable equity tracking and risk metrics on your dashboard</p>
              </div>
            </div>
            <Link
              to="/settings?tab=account"
              className="text-sm font-medium text-primary hover:text-primary/80 transition-colors whitespace-nowrap"
            >
              Set up &rarr;
            </Link>
          </div>
        )}

        {/* ── Performance Snapshot ── */}
        <section>
          <SectionLabel>Performance Snapshot</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            {statsCards.map((card, idx) => (
              <Card key={idx} className="flex items-center gap-4 p-5">
                <div className={`p-2.5 rounded-lg flex-shrink-0 ${card.iconBg}`}>
                  <span className={card.iconColor}>{card.icon}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-muted-foreground leading-none mb-1.5">{card.title}</p>
                  <p className={`text-2xl font-bold font-mono tabular-nums tracking-tight leading-none ${card.valueColor}`}>
                    {card.value}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* ── Today & Goals ── */}
        <section>
          <SectionLabel>Today &amp; Goals</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TodaySession trades={trades} />
            <GoalTracker
              goals={goals}
              progress={dashboardData?.goal_progress || []}
            />
            <PlanAdherence data={dashboardData?.plan_adherence || {}} />
          </div>
        </section>

        {/* ── Portfolio ── */}
        <section>
          <SectionLabel>Portfolio Curve</SectionLabel>
          <EquityCurve trades={trades} startingBalance={user?.starting_balance} />
        </section>

        {/* ── Risk Overview ── */}
        {dashboardData?.risk_metrics && (
          <section>
            <SectionLabel>Risk Overview</SectionLabel>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <AccountEquityCard metrics={dashboardData.risk_metrics} />
              <DrawdownWidget metrics={dashboardData.risk_metrics} />
              <RiskPerTradeWidget metrics={dashboardData.risk_metrics} />
            </div>
          </section>
        )}

        {/* ── Recent Activity ── */}
        <section>
          <SectionLabel>Recent Activity</SectionLabel>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pair</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Direction</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Profit</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pips</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {trades.slice(0, 5).map((trade, idx) => (
                    <tr key={idx} className="hover:bg-secondary/40 transition-colors duration-100">
                      <td className="px-4 py-3 text-muted-foreground">
                        {trade.trade_date ? new Date(trade.trade_date).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 font-medium text-foreground">{trade.symbol}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                          trade.direction?.toLowerCase().includes('buy')
                            ? 'bg-[rgba(34,197,94,0.12)] text-[#4ADE80] border border-[rgba(34,197,94,0.20)]'
                            : 'bg-[rgba(239,68,68,0.12)] text-[#F87171] border border-[rgba(239,68,68,0.20)]'
                        }`}>
                          {trade.direction || '—'}
                        </span>
                      </td>
                      <td className={`px-4 py-3 text-right font-semibold font-mono tabular-nums ${
                        isPositiveProfit(trade.profit) ? 'text-[#4ADE80]' : 'text-[#F87171]'
                      }`}>
                        {isPositiveProfit(trade.profit) ? '+' : ''}{formatProfit(trade.profit)}
                      </td>
                      <td className="px-4 py-3 text-right text-muted-foreground tabular-nums">{trade.pips ?? '—'}</td>
                    </tr>
                  ))}
                  {trades.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground text-sm">No trades yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </section>

      </div>
    </div>
  );
};

export default Dashboard;
